<script setup>
import { ref, onMounted, computed } from "vue";
import SearchableSelect from "@/components/SearchableSelectComponent.vue";
import useActionButtonsCommon from "@/composables/ActionButtonsCommon";

const { store, loading, _error, selectedDevices, selectedDevicesGrouped } =
  useActionButtonsCommon();

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(["update:modelValue"]);

const targetCourse = ref(null);

const courses = computed(() => store.getters["courses/getCourseNames"]);

const closeModal = () => {
  _error.value = null;
  loading.value = false;
  emit("update:modelValue", false);
};

onMounted(() => {
  store.dispatch("courses/fetchCourseNames", { primary: 1 });
});

const moveDevices = async () => {
  if (targetCourse.value && targetCourse.value !== "Select the courses") {
    const payload = {
      macs: selectedDevices.value.map((s) => s.mac),
      courses: selectedDevices.value.map((s) => s.server_name),
      server_name: targetCourse.value.url,
    };
    try {
      loading.value = true;
      await store.dispatch("windtagDevices/moveWindtagDevices", payload);

      closeModal();
    } catch (error) {
      loading.value = false;
      _error.value =
        error?.message ||
        `Failed to move device${selectedDevices.value.length > 1 ? "s" : ""}`;
    }
  }
};
</script>

<template>
  <div v-if="modelValue">
    <div class="modal fade show d-block" tabindex="-1" role="dialog">
      <div class="modal-backdrop fade show" @click="closeModal"></div>
      <div class="modal-dialog">
        <div class="modal-content bg-dark text-white">
          <div class="modal-header border-0">
            <h4 class="modal-title">Move Device(s)</h4>
            <button type="button" class="close text-white" @click="closeModal">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div class="modal-body">
            <div class="row">
              <div class="col-md-6">
                <h5 class="text-danger mb-4">FROM</h5>
                <div class="selected-devices">
                  <div
                    v-for="[key, course] in Object.entries(
                      selectedDevicesGrouped
                    )"
                    :key="key"
                  >
                    <p class="font-weight-bold">
                      {{ key === "null" ? "Unassigned" : key }}
                    </p>

                    <ul class="list-unstyled mb-0 pl-3">
                      <li v-for="device in course" :key="device.id">
                        Tag: {{ device.tag }} <br />
                        MAC: {{ device.mac }}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <h5 class="text-success mb-4">TO</h5>
                <div class="form-group">
                  <SearchableSelect
                    v-model="targetCourse"
                    :options="courses"
                    :label="'name'"
                    :trackBy="'id'"
                    :placeholder="'Select the courses'"
                  />
                </div>
              </div>
            </div>
            <p v-if="_error" class="text-danger">{{ _error }}</p>
          </div>

          <div class="modal-footer border-0">
            <button type="button" class="btn btn-danger" @click="closeModal">
              CLOSE
            </button>
            <button
              v-if="!loading"
              type="button"
              class="btn btn-success"
              @click="moveDevices"
              :disabled="!targetCourse || targetCourse === 'Select the courses'"
            >
              MOVE DEVICE{{ selectedDevices.length > 1 ? "S" : "" }}
            </button>
            <div
              v-if="loading"
              class="justify-content-center align-items-center"
              style="text-align: center"
            >
              <font-awesome-icon :icon="['fas', 'spinner']" spin size="2x" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import "@/styles/modal.css";

.form-control {
  border: none;
}

.form-control:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

select.form-control {
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3e%3cpath fill='%23ffffff' d='M2 0L0 2h4zm0 5L0 3h4z'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 8px 10px;
  padding-right: 1.75rem;
}

.current-course {
  color: #fff;
  font-size: 1.1rem;
}

.selected-devices {
  color: #fff;
  font-size: 1rem;
}
</style>
``
